import type { ItemNumber, Scene } from "@supermanus/document-model";
import { joinMergedLabels } from "@supermanus/document-model";
import { useEffect, useRef, useState } from "react";
import { handleFieldEnterKey } from "./fieldKeyboard";

export type SceneMergeIdentity = {
  title: string;
  number?: ItemNumber;
};

type Choice = "keep" | "other" | "both" | "custom";

function untitled(scene: Scene): string {
  return scene.title.trim() || "Unavngiven scene";
}

export function SceneMergeDialog({
  keep,
  other,
  otherIsFirst,
  customNumbers,
  onConfirm,
  onCancel,
}: {
  keep: Scene;
  other: Scene;
  otherIsFirst: boolean;
  customNumbers: boolean;
  onConfirm: (identity: SceneMergeIdentity) => void;
  onCancel: () => void;
}) {
  const [choice, setChoice] = useState<Choice>("keep");
  const [customTitle, setCustomTitle] = useState("");
  const customRef = useRef<HTMLInputElement>(null);
  const first = otherIsFirst ? other : keep;
  const second = otherIsFirst ? keep : other;
  const keepTitle = untitled(keep);
  const otherTitle = untitled(other);
  const bothTitle = joinMergedLabels(untitled(first), untitled(second));
  const bothNumber = joinMergedLabels(first.number, second.number);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCancel();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  useEffect(() => {
    if (choice === "custom") customRef.current?.focus();
  }, [choice]);

  function confirm() {
    if (choice === "keep") {
      onConfirm({
        title: keep.title,
        number: customNumbers ? keep.number : undefined,
      });
      return;
    }
    if (choice === "other") {
      onConfirm({
        title: other.title,
        number: customNumbers ? other.number : undefined,
      });
      return;
    }
    if (choice === "both") {
      onConfirm({
        title: bothTitle,
        number: customNumbers ? bothNumber : undefined,
      });
      return;
    }
    const title = customTitle.trim();
    if (!title) return;
    onConfirm({
      title,
      number: customNumbers ? keep.number : undefined,
    });
  }

  const canConfirm = choice !== "custom" || Boolean(customTitle.trim());

  return (
    <div className="sm-dialog-backdrop" role="presentation" onClick={onCancel}>
      <div
        className="sm-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sm-scene-merge-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="sm-scene-merge-title">Flet scener</h2>
        <p>
          Vælg navn
          {customNumbers ? " og nummer" : ""} på den flettede scene.
        </p>
        <ul className="sm-dialog-choice-list">
          <li>
            <label className={choice === "keep" ? "is-selected" : undefined}>
              <input
                type="radio"
                name="sm-merge-identity"
                checked={choice === "keep"}
                onChange={() => setChoice("keep")}
              />
              <span>
                <strong>{keepTitle}</strong>
                {customNumbers ? (
                  <span className="sm-dialog-choice-preview">
                    Nr. {keep.number}
                  </span>
                ) : null}
              </span>
            </label>
          </li>
          <li>
            <label className={choice === "other" ? "is-selected" : undefined}>
              <input
                type="radio"
                name="sm-merge-identity"
                checked={choice === "other"}
                onChange={() => setChoice("other")}
              />
              <span>
                <strong>{otherTitle}</strong>
                {customNumbers ? (
                  <span className="sm-dialog-choice-preview">
                    Nr. {other.number}
                  </span>
                ) : null}
              </span>
            </label>
          </li>
          <li>
            <label className={choice === "both" ? "is-selected" : undefined}>
              <input
                type="radio"
                name="sm-merge-identity"
                checked={choice === "both"}
                onChange={() => setChoice("both")}
              />
              <span>
                <strong>{bothTitle}</strong>
                {customNumbers ? (
                  <span className="sm-dialog-choice-preview">
                    Nr. {bothNumber}
                  </span>
                ) : null}
              </span>
            </label>
          </li>
          <li>
            <label className={choice === "custom" ? "is-selected" : undefined}>
              <input
                type="radio"
                name="sm-merge-identity"
                checked={choice === "custom"}
                onChange={() => setChoice("custom")}
              />
              <span>
                <strong>Nyt navn</strong>
                <input
                  ref={customRef}
                  type="text"
                  className="sm-dialog-text"
                  value={customTitle}
                  placeholder="Skriv en titel"
                  aria-label="Ny scenetitel"
                  onFocus={() => setChoice("custom")}
                  onChange={(e) => setCustomTitle(e.target.value)}
                  onKeyDown={(e) => {
                    handleFieldEnterKey(e);
                    if (e.key === "Enter" && customTitle.trim()) confirm();
                  }}
                />
              </span>
            </label>
          </li>
        </ul>
        <div className="sm-dialog-actions">
          <button type="button" onClick={onCancel}>
            Annuller
          </button>
          <button
            type="button"
            className="sm-dialog-primary"
            disabled={!canConfirm}
            onClick={confirm}
          >
            Flet
          </button>
        </div>
      </div>
    </div>
  );
}
